import React, { useState } from 'react';
import { Box, Button, Typography, FormControl, InputLabel, TextField, styled } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import UploadIcon from "../../../styles/svg/upload-file.svg";
import useCustomMutate from '../../../hooks/useMutate';
import { uploadBannerApi } from '../../../sevices/bannerApi';

const UploadButton = styled(Button)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  backgroundColor: "#FBFBFF",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  boxShadow: "none",
  flexDirection: "column",
  gap: "8px",
}));

const schema = yup.object({
  banner_file: yup
    .mixed()
    .required('Banner file is required')
    .test('fileSize', 'File size must be less than 400KB', (value) => {
      return value && value.size <= 400 * 1024;
    })
    // Optionally, add image dimension validation here if needed
});

const BroadcastBanner = ({ onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const { mutate: uploadBanner, isPending } = useCustomMutate(uploadBannerApi);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { banner_file: null },
  });

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append('banner_file', data.banner_file);
    uploadBanner(formData, {
      onSuccess: () => {
        reset();
        setSelectedFile(null);
        onClose();
      },
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setValue('banner_file', file, { shouldValidate: true });
    }
  };

  const handleCancel = () => {
    reset();
    setSelectedFile(null);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel shrink>Banner</InputLabel>
          <label htmlFor="banner-upload">
            <input
              id="banner-upload"
              type="file"
              accept="image/*"
              {...register("banner_file")}
              onChange={handleFileChange}
              style={{ display: "none" }}
              error={!!errors.banner_file}
            />
            <UploadButton
              variant="outlined"
              component="span"
              className="upload-btn"
            >
              <img src={UploadIcon} alt="upload" />
              <Typography variant="body1" sx={{ mt: 2, color: "#637381" }}>
                {selectedFile && selectedFile.name
                  ? selectedFile.name
                  : "Upload file"}
              </Typography>
            </UploadButton>
          </label>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            Please upload an image with a size of approx. 1600x350 pixels and
            less than 400KB in file size
          </Typography>
          {errors.banner_file && (
            <Typography variant="caption" color="error">
              {errors.banner_file.message}
            </Typography>
          )}
        </FormControl>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isPending}
          sx={{ borderRadius: "4px", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isPending}
          sx={{
            borderRadius: "4px",
            backgroundColor: "#F97316",
            "&:hover": { backgroundColor: "#EA580C" },
            textTransform: "none",
          }}
        >
          {isPending ? "Submitting..." : "Submit"}
        </Button>
      </Box>
    </form>
  );
};

export default BroadcastBanner; 